import { types } from "mobx-state-tree";
import fetcher, { setFetcherToken, clearFetcherToken } from "../utils/fetcher";
import productsStore from "./productsStore";
import { persist } from "../utils/persist";

const UserDetails = types.model("UserDetails", {
  USERNAME: types.optional(types.string, ""),
  FULLNAME: types.optional(types.string, ""),
  CELLPHONE: types.maybeNull(types.string, ""),
  ZCR_USERID: types.maybeNull(types.number, 0),
  ZCR_USERB: types.maybeNull(types.number, 0),
  ACP_ADDRESS: types.optional(types.string, ""),
  ACP_STATEA: types.optional(types.string, ""),
  ACP_STATENAME: types.optional(types.string, ""),
  CUST: types.frozen(),
  CUSTNAME: types.optional(types.string, ""),
  CUSTDES: types.optional(types.string, ""),
  token: types.optional(types.string, ""),
  USERLOGIN: types.maybeNull(types.string, ""),
  PROJECT: types.optional(types.string, ""),
});

const AppUserDetails = types.model("AppUserDetails", {
  USERNAME: types.optional(types.string, ""),
  FULLNAME: types.optional(types.string, ""),
  CELLPHONE: types.maybeNull(types.string, ""),
  ZCR_USERID: types.maybeNull(types.number, 0),
  ZCR_USERB: types.maybeNull(types.number, 0),
  ACP_ADDRESS: types.optional(types.string, ""),
  ACP_STATEA: types.optional(types.string, ""),
  ACP_STATENAME: types.optional(types.string, ""),
  CUST: types.frozen(),
  CUSTNAME: types.optional(types.string, ""),
  CUSTDES: types.optional(types.string, ""),
  token: types.optional(types.string, ""),
  USERLOGIN: types.maybeNull(types.string, ""),
  PROJECT: types.optional(types.string, ""),
});

const UserStore = types
  .model("UserStore", {
    userDetails: UserDetails,
    appUserDetails: AppUserDetails,
    error: types.frozen(),
    isLoading: types.optional(types.boolean, false),
    language: types.optional(types.string, "he"),
    isAuthenticated: types.optional(types.boolean, false),
    isAppAuthenticated: types.optional(types.boolean, false),
    projects: types.frozen(),
    cities: types.frozen(),
    countries: types.frozen(),
    positions: types.frozen(),
    zones: types.frozen(),
    selectedAppProject: types.frozen(),
  })
  .actions((self) => {
    function logout() {
      self.setUserDetails({});
      productsStore.setFamilyTypes([]);
      self.setSelectedProject(null);
      setIsAuthenticated(false);
      clearFetcherToken();
      window.localStorage.removeItem("acp-user");
    }
    function appLogout() {
      self.setAppUserDetails({});
      setIsAppAuthenticated(false);
      window.localStorage.removeItem("acp-app-user");
    }

    async function appLogin(data) {
      self.setError("");
      self.setAppUserDetails({});
      setLoading(true);
      window.localStorage.removeItem("acp-app-user");
      const dataBody = data;
      const response = await fetcher().post(dataBody).from.auth.login();

      self.setLoading(false);

      if (response?.status !== 200) {
        self.setIsAppAuthenticated(false);
        const status = response?.status;
        const errorText =
          status === 404
            ? "error:error_failed_login_user_not_exists"
            : status === 401
              ? "error:error_failed_login_wrong_cardential"
              : status === 400
                ? "error:error_failed_user_error_login_req"
                : "error:error_failed_login_req";
        self.setError(errorText);

        return {
          isSucceed: false,
          error: errorText,
        };
      }

      let userDetails = {
        ...data,
        ...response.data,
      };
      self.setAppUserDetails(userDetails, response.data);
      self.setIsAppAuthenticated(true);

      window.localStorage.setItem(
        "acp-app-user",
        JSON.stringify({
          appUserDetails: self.appUserDetails,
          isAppAuthenticated: self.isAppAuthenticated,
        })
      );
      return {
        isSucceed: true,
        data: self.appUserDetails,
      };
    }

    async function login(data) {
      self.setError("");
      self.setUserDetails({});
      setLoading(true);
      window.localStorage.removeItem("acp-user");
      const dataBody = data;
      const response = await fetcher().post(dataBody).from.auth.login();

      self.setLoading(false);

      if (response?.status !== 200) {
        self.setIsAuthenticated(false);
        const status = response?.status;
        const errorText =
          status === 404
            ? "error:error_failed_login_user_not_exists"
            : status === 401
              ? "error:error_failed_login_wrong_cardential"
              : status === 400
                ? "error:error_failed_user_error_login_req"
                : "error:error_failed_login_req";
        self.setError(errorText);

        return {
          isSucceed: false,
          error: errorText,
        };
      }

      let userDetails = {
        ...data,
        ...response.data,
      };
      self.setUserDetails(userDetails, response.data);
      self.setIsAuthenticated(true);
      setFetcherToken(response.data.token);

      window.localStorage.setItem(
        "acp-user",
        JSON.stringify({
          userDetails: self.userDetails,
          isAuthenticated: self.isAuthenticated,
        })
      );
      return {
        isSucceed: true,
        data: self.userDetails,
      };
    }

    async function setLanguage(lang) {
      self.language = lang;
      localStorage.setItem(
        "acp-user",
        JSON.stringify({
          isAuthenticated: self.isAuthenticated,
          userDetails: self.userDetails,
        })
      );
    }

    async function register(data) {
      self.setError("");
      self.setUserDetails({});
      setLoading(true);
      window.localStorage.removeItem("acp-user");
      const dataBody = data;
      console.log("dataBody", dataBody);
      const response = await fetcher().post(dataBody).from.common.general_load();

      self.setLoading(false);

      if (response?.status === 200 && !!response.data) {
        return response.data;
      } else {
        const errorText = "error:error_failed_register_req";
        self.setError(errorText);
      }
    }

    async function resetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.auth.reset_password();

      if (response?.status !== 200) {
        const errorText =
          response?.status === 404
            ? "error:error_failed_user_not_exists"
            : "error:error_failed_reset_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function sendResetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.auth.send_reset_password();
      if (response?.status !== 200) {
        var errorText = "error:error_failed_send_reset_req";
        if (response?.status === 404) {
          errorText = "error:error_failed_login_user_not_exists";
        }
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function getUserActiveProjects(data) {
      self.setError("");
      const response = await fetcher().post(data).from.projects.get_cust_projects();
      if (response?.status === 200 && response?.data) {
        self.setProjects(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:error_failed_get_cust_projects"
        );
      }
    }

    async function getCities() {
      self.setError("");
      const response = await fetcher().get.from.common.get_cities();
      if (response?.status === 200 && response?.data) {
        self.setCities(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:get_cities"
        );
      }
    }

    async function getPositions() {
      self.setError("");
      const response = await fetcher().get.from.common.get_positions();
      if (response?.status === 200 && response?.data) {
        self.setPositions(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:get_positions"
        );
      }
    }

    async function getCustomerTypes() {
      self.setError("");
      const response = await fetcher().get.from.common.get_customer_types();
      if (response?.status === 200 && response?.data) {
        self.setCities(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:get_customer_types"
        );
      }
    }

    async function getCountries() {
      self.setError("");
      const response = await fetcher().get.from.common.get_countries();
      if (response?.status === 200 && response?.data) {
        self.setCountries(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:get_countries"
        );
      }
    }
    async function getZones() {
      self.setError("");
      const response = await fetcher().get.from.common.get_zones();
      if (response?.status === 200 && response?.data) {
        self.setZones(response.data);
        return response.data;
      } else {
        self.setError(
          response?.status === 400 || response?.status === 500
            ? response?.data?.FORM?.InterfaceErrors?.text
            : "error:get_zones"
        );
      }
    }

    function setError(error) {
      self.error = error;
    }

    function setUserDetails(userDetailsData) {
      self.userDetails = { ...self.userDetails, ...userDetailsData };
      self.userDetails.full_name = self.userDetails.first_name + " " + self.userDetails.last_name;
    }

    function setAppUserDetails(appUserDetailsData) {
      self.appUserDetails = { ...self.appUserDetails, ...appUserDetailsData };
      self.appUserDetails.full_name =
        self.appUserDetails.first_name + " " + self.appUserDetails.last_name;
    }

    function setLoading(loading) {
      self.isLoading = loading;
    }

    function setIsAuthenticated(authenticated) {
      self.isAuthenticated = authenticated;
    }
    function setIsAppAuthenticated(authenticated) {
      self.isAppAuthenticated = authenticated;
    }

    function setProjects(projects) {
      self.projects = projects;
    }

    function setCities(cities) {
      self.cities = cities;
    }
    function setCountries(conutries) {
      self.countries = conutries;
    }
    function setZones(zones) {
      self.zones = zones;
    }

    function setPositions(positions) {
      self.positions = positions;
    }

    function setSelectedProject(selectedProject) {
      self.selectedProject = selectedProject;
    }

    function setAppSelectedProject(selectedProject) {
      self.selectedAppProject = selectedProject;
    }

    return {
      login,
      appLogin,
      register,
      logout,
      appLogout,
      setLoading,
      setUserDetails,
      setAppUserDetails,
      setError,
      setLanguage,
      getCities,
      getCountries,
      getZones,
      setIsAuthenticated,
      setIsAppAuthenticated,
      resetPassword,
      sendResetPassword,
      setCities,
      setProjects,
      setCountries,
      setZones,
      getCustomerTypes,
      getPositions,
      setPositions,
      getUserActiveProjects,
      setSelectedProject,
      setAppSelectedProject,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
    getUser() {
      return self.toJSON();
    },
    get vars() {
      return self.userDetails.toJSON();
    },
  }));

const localStorageUser = JSON.parse(localStorage.getItem("acp-user"));
const appLocalStorageUser = JSON.parse(localStorage.getItem("acp-app-user"));

if (localStorageUser?.userDetails?.token) {
  setFetcherToken(localStorageUser.userDetails.token);
}

const userStore = UserStore.create({
  userDetails: localStorageUser?.userDetails || {},
  appUserDetails: appLocalStorageUser?.appUserDetails || {},
  error: "",
  isLoading: false,
  allContracts: false,
  isAuthenticated: localStorageUser?.isAuthenticated || false,
  isAppAuthenticated: appLocalStorageUser?.isAppAuthenticated || false,
  customers: [],
  positions: [],
  selectedProject: localStorageUser?.selectedProject,
  selectedAppProject: localStorageUser?.selectedAppProject,
});

persist("acp-userStore", userStore, {
  storage: window.sessionStorage,
  jsonify: true,
});

// Sync cachedToken with the final store state after persist/applySnapshot,
// since applySnapshot may restore a different token than what was in localStorage.
if (userStore.userDetails?.token) {
  setFetcherToken(userStore.userDetails.token);
}

export default userStore;
